/**
 * Mesure du fond de l'accueil tel qu'il s'affiche (DOM / canvas), puis calcul
 * des contrastes via `contrast.ts`.
 *
 * Le fond est fixe et `cover` sur la fenêtre : on l'échantillonne à la
 * proportion de l'écran courant, réduit à 40 px de large — à cette échelle un
 * pixel ≈ la zone moyennée par le backdrop-blur de 50 px des blocs.
 */
import {
  type DashboardBackground,
  type GradientPreset,
  contrastForNeutral,
  findGradientPreset,
} from "./background";
import {
  type BackgroundContrast,
  contrastForColor,
  contrastFromPixels,
  FALLBACK_CONTRAST,
  type Rgb,
} from "./contrast";

const SAMPLE_WIDTH = 40;
/** Hauteur approximative du header, pour ne mesurer que la bande qu'il recouvre. */
const HEADER_HEIGHT_PX = 72;
const FALLBACK_VIEWPORT = { width: 1440, height: 900 };

function viewport() {
  if (typeof window === "undefined" || !window.innerHeight) {
    return FALLBACK_VIEWPORT;
  }
  return { width: window.innerWidth, height: window.innerHeight };
}

function createSampleCanvas(aspect: number) {
  const canvas = document.createElement("canvas");
  canvas.width = SAMPLE_WIDTH;
  canvas.height = Math.max(1, Math.round(SAMPLE_WIDTH / aspect));
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  return { canvas, ctx };
}

function readPixels(ctx: CanvasRenderingContext2D, w: number, h: number) {
  const { data } = ctx.getImageData(0, 0, w, h);
  const pixels: Rgb[] = [];
  for (let i = 0; i < data.length; i += 4) {
    pixels.push([data[i], data[i + 1], data[i + 2]]);
  }
  return pixels;
}

/** Contraste d'un échantillon : tout le fond pour les blocs, la bande du haut pour le header. */
function contrastOfSample(
  ctx: CanvasRenderingContext2D,
  canvas: HTMLCanvasElement,
): BackgroundContrast {
  const { width, height } = canvas;
  const pixels = readPixels(ctx, width, height);
  const headerRows = Math.max(
    1,
    Math.min(
      height,
      Math.round((height * HEADER_HEIGHT_PX) / viewport().height),
    ),
  );
  return contrastFromPixels(pixels, pixels.slice(0, headerRows * width));
}

export function contrastForGradient(
  preset: GradientPreset,
): BackgroundContrast {
  const { width, height } = viewport();
  const { canvas, ctx } = createSampleCanvas(width / height);
  if (!ctx) return FALLBACK_CONTRAST;
  const { width: w, height: h } = canvas;
  // Ligne de dégradé CSS : passe par le centre, longueur telle que les coins
  // extrêmes reçoivent les couleurs 0 % et 100 %.
  const rad = (preset.angle * Math.PI) / 180;
  const dx = Math.sin(rad);
  const dy = -Math.cos(rad);
  const half = (Math.abs(w * dx) + Math.abs(h * dy)) / 2;
  const gradient = ctx.createLinearGradient(
    w / 2 - dx * half,
    h / 2 - dy * half,
    w / 2 + dx * half,
    h / 2 + dy * half,
  );
  for (const stop of preset.stops) {
    gradient.addColorStop(stop.at / 100, stop.color);
  }
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, w, h);
  return contrastOfSample(ctx, canvas);
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    if (!src.startsWith("blob:") && !src.startsWith("data:")) {
      img.crossOrigin = "anonymous";
    }
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("image load failed"));
    img.src = src;
  });
}

/**
 * Mesure une image telle qu'elle s'affichera en `background-size: cover` sur
 * l'écran courant. À appeler sur le fichier local (URL `blob:`) avant l'envoi :
 * une URL S3 signée d'un autre domaine rendrait le canvas illisible (CORS).
 */
export async function contrastForImage(
  src: string,
): Promise<BackgroundContrast> {
  try {
    const img = await loadImage(src);
    const { width, height } = viewport();
    const aspect = width / height;
    // Recadrage `cover` centré.
    let sw = img.naturalWidth;
    let sh = img.naturalHeight;
    if (sw / sh > aspect) sw = sh * aspect;
    else sh = sw / aspect;
    const sx = (img.naturalWidth - sw) / 2;
    const sy = (img.naturalHeight - sh) / 2;

    // Réduction en deux temps : un seul drawImage vers 40 px échantillonne au
    // lieu de moyenner, ce qui rate le flou du backdrop-blur.
    const mid = document.createElement("canvas");
    mid.width = SAMPLE_WIDTH * 8;
    mid.height = Math.max(1, Math.round(mid.width / aspect));
    const midCtx = mid.getContext("2d");
    const { canvas, ctx } = createSampleCanvas(aspect);
    if (!midCtx || !ctx) return FALLBACK_CONTRAST;
    midCtx.imageSmoothingQuality = "high";
    midCtx.drawImage(img, sx, sy, sw, sh, 0, 0, mid.width, mid.height);
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(mid, 0, 0, canvas.width, canvas.height);
    return contrastOfSample(ctx, canvas);
  } catch {
    return FALLBACK_CONTRAST;
  }
}

/**
 * Contraste du fond affiché. Une image n'est pas re-mesurée : sa mesure a été
 * faite à l'envoi et enregistrée avec la préférence. Sans image disponible
 * (supprimée, pas encore chargée), le fond neutre est affiché.
 */
export function resolveBackgroundContrast(
  background: DashboardBackground,
  hasImage: boolean,
): BackgroundContrast {
  switch (background.type) {
    case "color":
      return contrastForColor(background.color);
    case "gradient":
      return contrastForGradient(findGradientPreset(background.gradientId));
    case "image":
      return hasImage ? background.contrast : contrastForNeutral();
    default:
      return contrastForNeutral();
  }
}
